import { AlertTriangle } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

export interface ConfirmActionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** e.g. "Suspend organization" */
  action: string;
  resource: { type: string; name: string; id: string };
  consequences?: ReactNode[];
  confirmLabel: string;
  destructive?: boolean;
  /** Require a reason (stored in the audit log). */
  requireReason?: boolean;
  /** Require typing the resource name before confirming. */
  requireTypedName?: boolean;
  pending?: boolean;
  onConfirm: (reason: string) => void;
}

/**
 * Confirmation for dangerous actions: shows the resource, the action, and known consequences.
 * The dialog stays open until the backend answers.
 */
export function ConfirmAction({
  open,
  onOpenChange,
  action,
  resource,
  consequences,
  confirmLabel,
  destructive = true,
  requireReason,
  requireTypedName,
  pending,
  onConfirm,
}: ConfirmActionProps) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (open) {
      setReason("");
      setTyped("");
    }
  }, [open]);
  const ready = (!requireReason || reason.trim().length >= 3) && (!requireTypedName || typed.trim() === resource.name);
  return (
    <AlertDialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {destructive ? <AlertTriangle className="size-4 text-danger" aria-hidden /> : null}
            {action}
          </AlertDialogTitle>
          <AlertDialogDescription render={<div />} className="space-y-3 text-left">
            <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 rounded-md border bg-muted/40 p-2.5 text-xs">
              <dt className="text-muted-foreground">Resource</dt>
              <dd className="font-medium text-foreground">
                {resource.type} · {resource.name}
              </dd>
              <dt className="text-muted-foreground">ID</dt>
              <dd className="break-all font-mono text-foreground">{resource.id}</dd>
              <dt className="text-muted-foreground">Action</dt>
              <dd className="text-foreground">{action}</dd>
            </dl>
            {consequences?.length ? (
              <div>
                <p className="mb-1 font-medium text-foreground text-xs">What happens</p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs">
                  {consequences.map((c, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static list
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {requireReason ? (
          <Field className="gap-1">
            <FieldLabel htmlFor="confirm-reason" className="text-xs">
              Reason (recorded in the audit log)
            </FieldLabel>
            <Textarea id="confirm-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="text-[13px]" />
          </Field>
        ) : null}
        {requireTypedName ? (
          <Field className="gap-1">
            <FieldLabel htmlFor="confirm-typed" className="text-xs">
              Type <span className="font-mono">{resource.name}</span> to confirm
            </FieldLabel>
            <Input id="confirm-typed" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
            <FieldDescription className="text-[11px]">This protects against acting on the wrong record.</FieldDescription>
          </Field>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <Button variant={destructive ? "destructive" : "default"} disabled={!ready || pending} onClick={() => onConfirm(reason.trim())}>
            {pending ? <Spinner data-icon="inline-start" /> : null}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
